/**
 * Pre-release listing modes — frontend mirror of backend/src/utils/pre-release.ts.
 * Keep the two in step; the strings are persisted on `Product.preReleaseMode`.
 *
 * - `draft`     — hidden from the storefront until the release date (default).
 * - `preorder`  — sellable now, presented as a pre-order (`[PRE-ORDER]` title,
 *                 `Pre-order` tag, release notice).
 * - `available` — sellable now, presented as ordinary stock. For stores allowed
 *                 to sell before street date: the stock really is on the shelf,
 *                 so a pre-order label would be a lie to the customer.
 *
 * `available` differs from `preorder` in presentation only. New Arrival windows
 * are unaffected — those are derived in the database from the release date.
 */

export const PRE_RELEASE_MODES = ['draft', 'preorder', 'available'] as const

export type PreReleaseMode = (typeof PRE_RELEASE_MODES)[number]

export function normalizePreReleaseMode(value: unknown): PreReleaseMode {
  return (PRE_RELEASE_MODES as readonly string[]).includes(String(value))
    ? (String(value) as PreReleaseMode)
    : 'draft'
}

/** Sellable before the release date? True for both non-draft modes. */
export function isPreReleaseSellable(value: unknown): boolean {
  const mode = normalizePreReleaseMode(value)
  return mode === 'preorder' || mode === 'available'
}
